import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Re-read the visible window at this granularity. Rebuilding for every scrolled
 * pixel would cost more than the work it saves; a bucket this size is far
 * smaller than the screen of overscan on either side, so scrolling never outruns
 * it.
 */
const SCROLL_BUCKET_PX = 200;

export interface VisibleXRange {
  /** Left edge in the SCALED coordinate space, one screen of overscan included. */
  from: number;
  /** Right edge, likewise. */
  to: number;
}

/**
 * The horizontal slice of a scrolling timeline the operator can actually see,
 * plus a screen either side.
 *
 * Zoom multiplies the axis width (`scaledWidth = width * zoom`, up to MAX_ZOOM
 * 500), and everything drawn on it — axis labels, event rects — was being built
 * for the whole width on every zoom step while only a fraction was on screen.
 * Editors draw what is visible; this is the window they draw.
 *
 * Returns null while there is nothing to scroll (live mode stretches the SVG to
 * 100% and never scrolls), which callers read as "no culling, render it all".
 */
export const useVisibleXRange = (
  containerRef: RefObject<HTMLDivElement | null> | undefined,
  { enabled, scaledWidth }: { enabled: boolean; scaledWidth: number },
): VisibleXRange | null => {
  const [range, setRange] = useState<VisibleXRange | null>(null);

  // Layout, not passive: a zoom step changes `scaledWidth` and `useTimelinePan`
  // moves `scrollLeft` to keep the same slice of time centred, both in the same
  // commit. Read after paint and the browser has already drawn one frame with
  // the window computed for the OLD geometry — several screens away on a big
  // step, so the overscan does not cover it and the events visibly pop in.
  // Reading here (and letting the resulting `setRange` flush before paint) puts
  // the right window in the first frame the zoom is visible in.
  useLayoutEffect(() => {
    const el = containerRef?.current;
    if (!el || !enabled) {
      setRange(null);
      return;
    }

    let frame: number | null = null;
    const read = () => {
      frame = null;
      const width = el.clientWidth;
      const left = Math.floor(el.scrollLeft / SCROLL_BUCKET_PX) * SCROLL_BUCKET_PX;
      const from = Math.max(0, left - width);
      const to = Math.min(scaledWidth, left + width * 2);
      setRange((prev) => (prev && prev.from === from && prev.to === to ? prev : { from, to }));
    };

    read();
    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(read);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(onScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      observer.disconnect();
      if (frame != null) cancelAnimationFrame(frame);
    };
    // `scaledWidth` re-runs this on zoom, so the window is re-read against the
    // new geometry rather than kept from the old.
  }, [containerRef, enabled, scaledWidth]);

  return range;
};
